import { splitSearchWords } from '~/chords/DropdownSingleSelect/constants';
import type {
  InternalDropdownOption,
  InternalDropdownOptions,
} from '~/chords/DropdownSingleSelect/types';
import {
  doesLabelMatchSearchTerm,
  findSearchTermWordCover,
} from '~/chords/DropdownSingleSelect/utils/findSearchTermWordCover';
import { foldSearchText } from '~/chords/DropdownSingleSelect/utils/foldSearchText';
import { getSearchMatchTokens } from '~/chords/DropdownSingleSelect/utils/getSearchMatchTokens';

const UNMATCHED_WORD_INDEX = 9999;

function getOptionSearchLabel(option: InternalDropdownOption): string {
  return foldSearchText(option.label || option.value);
}

/**
 * Higher values rank first. Tuple is compared left to right:
 *   - exact match
 *   - label prefix
 *   - number of full-word term matches
 *   - number of matching terms
 *   - earlier matching word
 *   - shorter label
 */
function getSearchRelevanceRank(
  option: InternalDropdownOption,
  searchQueryFolded: string,
  searchTerms: string[],
): number[] {
  const label = getOptionSearchLabel(option);
  const words = splitSearchWords(label);

  let fullWordMatchCount = 0;
  let matchedTermCount = 0;
  let earliestWordIndex = UNMATCHED_WORD_INDEX;

  for (const term of searchTerms) {
    const cover = findSearchTermWordCover(term, words);

    if (!cover) {
      if (doesLabelMatchSearchTerm(label, term)) {
        matchedTermCount += 1;
      }

      continue;
    }

    matchedTermCount += 1;
    earliestWordIndex = Math.min(
      earliestWordIndex,
      ...cover.map((part) => part.wordIndex),
    );

    const isFullWordCover = cover.every(
      (part) => part.length === words[part.wordIndex].length,
    );

    if (isFullWordCover) {
      fullWordMatchCount += 1;
    }
  }

  return [
    label === searchQueryFolded ? 1 : 0,
    label.startsWith(searchQueryFolded) ? 1 : 0,
    fullWordMatchCount,
    matchedTermCount,
    -earliestWordIndex,
    -label.length,
  ];
}

function compareRankedOptions(
  a: { option: InternalDropdownOption; rank: number[] },
  b: { option: InternalDropdownOption; rank: number[] },
): number {
  for (let index = 0; index < a.rank.length; index++) {
    if (a.rank[index] !== b.rank[index]) {
      return b.rank[index] - a.rank[index];
    }
  }

  return a.option.optionIndex - b.option.optionIndex;
}

/**
 * Reorder options within each group by how well the label matches the query.
 * Does not flatten groups or rewrite optionIndex.
 *
 *   - Search is accent-insensitive: axe matches Axé, forro matches Forró.
 *   - Queries match across joiners: hiphop matches Hip-Hop and HipHop, dumb matches "D.U.M.B.", dba matches d/b/a.
 *   - Apostrophes are joiners: childrens matches Children's, drumnbass matches Drum'n'bass.
 *   - Concatenated words can match in any order: hiphoprap matches Hip Hop/Rap and Rap/Hip Hop.
 */
export function sortOptionsBySearchRelevance(
  options: InternalDropdownOptions,
  searchQuery: string,
): InternalDropdownOptions {
  const searchQueryFolded = foldSearchText(searchQuery);
  const searchTerms = getSearchMatchTokens(searchQueryFolded);

  return options.map((group) => {
    const groupOptions = 'options' in group ? group.options : group;
    const rankedOptions = groupOptions.map((option) => ({
      option,
      rank: getSearchRelevanceRank(option, searchQueryFolded, searchTerms),
    }));
    const sortedOptions = rankedOptions
      .sort(compareRankedOptions)
      .map(({ option }) => option);

    if ('options' in group) {
      return {
        ...group,
        options: sortedOptions,
      };
    }

    return sortedOptions;
  });
}
